import { flatten, map, uniqBy } from 'lodash';
import React, {
  useRef,
  useState,
  forwardRef,
  useEffect,
  useCallback,
} from 'react';
import { combineClassNames } from '../../../../core/css';
import { SvgIcon } from '../../../../core/icons';
import { useTranslate } from '../../../../core/translate';

export const normalizeSearchFeatures = (compareGroups) => {
  return compareGroups
    ? uniqBy(
        flatten(
          map(compareGroups, (group, groupKey) => {
            return map(group, (feature, featureKey) => {
              return {
                group: groupKey,
                feature: featureKey,
                description: feature.description,
              };
            });
          })
        ),
        'description'
      )
    : [];
};

const CompareSearchInput = forwardRef((props, ref) => {
  const {
    searchTerm,
    searchSuggestions,
    onSearchTermChange,
    onSearchSuggestionSelection,
    highlightedIndex,
    setHighlightedIndex,
    setIsFocused,
    setHideSuggestions,
    analyticsKey,
  } = props;
  const translate = useTranslate();
  const ariaLabel = translate('compare.search.input.aria.label.v3');
  const placeholderLabel = translate('compare.search.input.placeholder.v3');
  const onKeyDown = (event) => {
    if (searchSuggestions.length === 0) {
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
        if (highlightedIndex === 0) {
          setHighlightedIndex(searchSuggestions.length - 1);
        } else {
          setHighlightedIndex(highlightedIndex - 1);
        }
        break;
      case 'ArrowDown':
        if (highlightedIndex === searchSuggestions.length - 1) {
          setHighlightedIndex(0);
        } else {
          setHighlightedIndex(highlightedIndex + 1);
        }
        break;
      case 'Enter':
        onSearchSuggestionSelection(searchSuggestions[highlightedIndex]);
        event.target.blur();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        setHideSuggestions(true);
        break;
    }
  };
  return (
    <input
      className={'compare-search__input stat-search-input'}
      placeholder={placeholderLabel}
      type={'search'}
      value={removeHtmlTags(searchTerm)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onChange={(event) => {
        onSearchTermChange(event.target.value);
        setHighlightedIndex(0);
        setHideSuggestions(false);
      }}
      onKeyDown={onKeyDown}
      ref={ref}
      role="combobox"
      aria-label={ariaLabel}
      data-dtm={`${analyticsKey}:feature search`}
    />
  );
});

CompareSearchInput.displayName = 'CompareSearchInput';

const CompareSearchSuggestions = (props) => {
  const {
    searchTerm,
    searchSuggestions,
    onSearchSuggestionSelection,
    renderSuggestion,
    highlightedIndex,
    setHighlightedIndex,
    isFocused,
  } = props;
  const translate = useTranslate();
  const suggestionDescription =
    !searchSuggestions || searchSuggestions.length === 0
      ? translate('compare.search.input.no.matches.v3')
      : translate('compare.search.numbered.matches.v3', {
          searchResultCount: searchSuggestions?.length,
        });
  const [suggestionBoxEl, setSuggestionBoxEl] = useState();
  const [suggestionBoxHeight, setSuggestionBoxHeight] = useState();
  const suggestionBoxRef = useCallback((node) => {
    setSuggestionBoxEl(node);
  });

  useEffect(() => {
    if (suggestionBoxEl) {
      setSuggestionBoxHeight(
        window.innerHeight - suggestionBoxEl.getBoundingClientRect().top
      );
    }
  }, [suggestionBoxEl, isFocused]);

  return (
    <>
      {isFocused && searchSuggestions.length > 0 && (
        <ol
          className={'compare-search__suggestions'}
          role="listbox"
          style={{ maxHeight: suggestionBoxHeight }}
          ref={suggestionBoxRef}>
          {searchSuggestions.map((suggestion, i) => (
            <li
              key={suggestion.item.feature}
              className={combineClassNames(
                'compare-search__suggestion',
                i === highlightedIndex
                  ? 'compare-search__suggestion--highlighted'
                  : null
              )}
              role={'option'}
              onMouseDown={() => {
                onSearchSuggestionSelection(suggestion);
              }}
              onMouseEnter={() => {
                setHighlightedIndex(i);
              }}>
              {renderSuggestion(searchTerm, suggestion)}
            </li>
          ))}
        </ol>
      )}
      <div
        className="compare-search_suggestions__aria-announcements"
        role="status"
        aria-live="assertive"
        aria-atomic="true">
        {suggestionDescription}
      </div>
    </>
  );
};

const CompareSearch = (props) => {
  const {
    searchTerm,
    onSearchTermChange,
    isHidden,
    isFocused,
    setIsFocused,
    analyticsKey,
  } = props;
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [hideSuggestions, setHideSuggestions] = useState(false);
  const searchInputRef = useRef();

  useEffect(() => {
    if (isHidden) {
      setIsFocused(false);
    }
  }, [isHidden]);

  useEffect(() => {
    if (isFocused && searchInputRef.current != document.activeElement) {
      searchInputRef.current?.focus();
    }
  }, [isFocused]);

  return (
    !isHidden && (
      <div className="compare-search">
        <div className={'compare-search__container'}>
          <button
            className="compare-search__search-button stat-button-link"
            data-dtm={`${analyticsKey}:search`}
            onClick={() => {
              onSearchTermChange(searchTerm);
              setIsFocused(true);
            }}>
            <SvgIcon
              name={'feature-search'}
              className="compare-search__search-icon"
            />
          </button>
          <CompareSearchInput
            {...props}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
            setHideSuggestions={setHideSuggestions}
            ref={searchInputRef}
          />
          {searchTerm && (
            <button
              className="compare-search__clear-button stat-button-link"
              data-dtm={`${analyticsKey}:clear search`}
              onClick={() => {
                onSearchTermChange('');
                setIsFocused(true);
              }}>
              <SvgIcon 
              name={'feature-close'} 
              className="compare-search__clear-icon"
              />
            </button>
          )}
        </div>
        {searchTerm && !hideSuggestions && (
          <CompareSearchSuggestions
            {...props}
            highlightedIndex={highlightedIndex}
            setHighlightedIndex={setHighlightedIndex}
          />
        )}
      </div>
    )
  );
};

const removeHtmlTags = (str) => {
  return str
    .replace(/<[^>]*>([^<]*)<\/[^>]*>/g, '')
    .replace(/&([^;\s]+);/g, '');
};

export default CompareSearch;
